import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Accordion, Button } from 'flowbite-react';
import { useTranslation } from 'react-i18next';
import MDEditor, { commands, ICommand } from '@uiw/react-md-editor';
import {
  ImageIcon,
  Copy,
  Check,
  ArrowLeft,
  ArrowRight,
  ChevronDown,
} from 'lucide-react';
import { useUserInfo } from '../auth/UserInfoProvider';
import {
  useGetIstiftaByIdQuery,
  useUpdateIstiftaStatusMutation,
  useReplyToIstiftaMutation,
} from '../redux/api/istiftaApi';
import { useCreateMediaMutation } from '../redux/api/mediaApi';
import { useAppSelector } from '../redux/hooks/useAppSelector';
import {
  selectIsDarkMode,
  selectIsRTL,
} from '../redux/selectors/displaySettingsSelectors';
import { getMediaType } from '../utils/getMediaType';
import { useMarkdownImageUpload } from '../hooks/useMarkdownImageUpload';
import { MediaPurpose } from '../common/enums/MediaPurpose.enum';
import { SupportStatus } from '../common/enums/SupportStatus';
import { Loading } from '../components/Shared/Loading/Loading';
import { ErrorComponent } from '../components/Shared/ErrorComponent/ErrorComponent';
import { usePageTitle } from '../hooks/usePageTitle';
import { statusStyles } from '../components/TechnicalSupport/ticketStyles';

const IstiftaDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userInfo } = useUserInfo();
  const isDarkMode = useAppSelector(selectIsDarkMode);
  const isRtl = useAppSelector(selectIsRTL);

  const [reply, setReply] = useState('');
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const {
    data: ticketData,
    isLoading,
    isError,
  } = useGetIstiftaByIdQuery(id ?? '', { skip: !id, pollingInterval: 15000 });

  const ticket = ticketData?.data;

  usePageTitle(
    ticket
      ? t('istifta.detailsPageTitle', { ticketNumber: ticket.ticketNumber })
      : t('sidebar.istifta')
  );

  useEffect(() => {
    setReply('');
    setIsPreviewMode(false);
  }, [id]);

  const [updateIstiftaStatus, { isLoading: isUpdatingStatus }] =
    useUpdateIstiftaStatusMutation();
  const [replyToIstifta, { isLoading: isSending }] =
    useReplyToIstiftaMutation();
  const [createMedia] = useCreateMediaMutation();

  const { textareaProps: pasteDropProps, isUploading } = useMarkdownImageUpload(
    {
      value: reply,
      onChange: setReply,
      purpose: MediaPurpose.SupportTicket,
    }
  );

  const handleStatusChange = async (status: SupportStatus) => {
    if (!ticket) return;
    await updateIstiftaStatus({ id: ticket.id, status }).unwrap();
  };

  const handleSendReply = async () => {
    if (!ticket || !reply.trim() || !userInfo) return;
    const message = reply.trim();
    setReply('');
    try {
      await replyToIstifta({
        ticketId: ticket.id,
        message,
        to: ticket.userId ?? ticket.userEmail,
        fromUser: { id: userInfo.id, name: userInfo.name },
      }).unwrap();
    } catch {
      setReply(message);
    }
  };

  const handleCopy = async (text: string, idx: number) => {
    await navigator.clipboard.writeText(text);
    setCopiedIndex(idx);
    setTimeout(() => setCopiedIndex((c) => (c === idx ? null : c)), 2000);
  };

  const createMediaRef = useRef(createMedia);
  createMediaRef.current = createMedia;

  const uploadImageCommand: ICommand = useMemo(
    () => ({
      name: 'upload-image',
      keyCommand: 'upload-image',
      buttonProps: {
        'aria-label': t('technicalSupport.uploadImage'),
        title: t('technicalSupport.uploadImage'),
      },
      icon: <ImageIcon size={13} />,
      execute: (_state, api) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async () => {
          const file = input.files?.[0];
          if (!file) return;
          const placeholder = `![${t('technicalSupport.uploadingImage')}]()`;
          api.replaceSelection(placeholder);
          try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('mediaType', getMediaType(file.type));
            fd.append('purpose', MediaPurpose.SupportTicket);
            // @ts-expect-error — RTK Query mutation types don't accept FormData directly
            const res = await createMediaRef.current(fd).unwrap();
            const currentText = api.textArea.value;
            const idx = currentText.indexOf(placeholder);
            if (idx >= 0)
              api.setSelectionRange({
                start: idx,
                end: idx + placeholder.length,
              });
            api.replaceSelection(`![](${res.url})`);
          } catch {
            const currentText = api.textArea.value;
            const idx = currentText.indexOf(placeholder);
            if (idx >= 0) {
              api.setSelectionRange({
                start: idx,
                end: idx + placeholder.length,
              });
              api.replaceSelection('');
            }
          }
        };
        input.click();
      },
    }),
    []
  );

  if (isLoading) return <Loading />;
  if (isError || !ticket) {
    return <ErrorComponent errorMessage={t('istifta.notFound')} />;
  }

  const BackIcon = isRtl ? ArrowRight : ArrowLeft;
  const editorClass = `md-reply-editor ${isRtl ? 'rtl' : 'ltr'}`;

  return (
    <div className="flex flex-col gap-6">
      {/* ── Page header ── */}
      <div className="flex flex-col gap-3 rounded-lg border border-gray-300 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-gray-800">
        <Button
          size="xs"
          color="light"
          onClick={() => navigate(-1)}
          className="w-fit"
        >
          <BackIcon size={14} className="me-1.5" aria-hidden="true" />
          {t('istifta.back')}
        </Button>

        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex flex-col gap-1.5">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {ticket.ticketNumber}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {[
                ticket.userEmail,
                new Date(ticket.createdAt).toLocaleString(isRtl ? 'ar' : 'en'),
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>

          {/* Status selector */}
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('istifta.status')}
            </span>
            <div className="relative w-fit">
              <select
                disabled={isUpdatingStatus}
                value={ticket.status}
                onChange={(e) =>
                  handleStatusChange(e.target.value as SupportStatus)
                }
                className={`cursor-pointer appearance-none rounded-full py-1 pe-7 ps-3 text-xs font-semibold ring-1 ring-inset focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-60 ${statusStyles[ticket.status].className}`}
              >
                {Object.values(SupportStatus).map((s) => (
                  <option key={s} value={s}>
                    {t('technicalSupport.statusList.' + s.toLowerCase())}
                  </option>
                ))}
              </select>
              <ChevronDown
                size={12}
                className="pointer-events-none absolute end-2 top-1/2 -translate-y-1/2"
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Original message ── */}
      <section
        aria-labelledby="istifta-msg-heading"
        className="rounded-lg border border-gray-300 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-gray-800"
      >
        <h2
          id="istifta-msg-heading"
          className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300"
        >
          {t('istifta.originalMessage')}
        </h2>
        <div className="auto-direction-text whitespace-pre-wrap text-gray-800 dark:text-gray-100">
          {ticket.message}
        </div>
      </section>

      {/* ── Replies ── */}
      <section
        aria-labelledby="istifta-replies-heading"
        className="flex flex-col gap-3 rounded-lg border border-gray-300 bg-white p-4 shadow-sm dark:border-gray-600 dark:bg-gray-800"
      >
        <h2
          id="istifta-replies-heading"
          className="text-sm font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300"
        >
          {t('istifta.replies')}
          {(ticket.replies ?? []).length > 0 && (
            <span className="ms-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              {ticket.replies!.length}
            </span>
          )}
        </h2>

        <div
          role="log"
          aria-live="polite"
          aria-label={t('istifta.replies')}
          className="flex max-h-[45vh] flex-col gap-2 overflow-y-auto"
        >
          {(ticket.replies ?? []).length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-400">
              {t('istifta.noReplies')}
            </p>
          ) : (
            (ticket.replies ?? []).map((r, i) => {
              const isMine = r.from === userInfo?.id;
              const isAdminReply = !ticket.userId || r.from !== ticket.userId;
              return (
                <div
                  key={i}
                  className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[75%] rounded-lg p-3 ${
                      isMine
                        ? 'bg-primary-100 dark:bg-primary-700'
                        : 'bg-gray-100 dark:bg-gray-700'
                    }`}
                  >
                    <div className="mb-1 flex items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span>
                        {r.fromUser?.name ?? r.from} ·{' '}
                        {new Date(r.sendAt).toLocaleString()}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleCopy(r.message, i)}
                        title={
                          copiedIndex === i
                            ? t('istifta.copied')
                            : t('istifta.copy')
                        }
                        aria-label={t('istifta.copy')}
                        className="flex shrink-0 items-center rounded p-1 hover:bg-black/5 dark:hover:bg-white/10"
                      >
                        {copiedIndex === i ? (
                          <Check size={14} />
                        ) : (
                          <Copy size={14} />
                        )}
                      </button>
                    </div>
                    {isAdminReply ? (
                      <div
                        className="markdown-reply auto-direction-text text-sm text-gray-900 dark:text-gray-100"
                        data-color-mode={isDarkMode ? 'dark' : 'light'}
                      >
                        <MDEditor.Markdown source={r.message} />
                      </div>
                    ) : (
                      <div className="auto-direction-text whitespace-pre-wrap text-sm text-gray-900 dark:text-gray-100">
                        {r.message}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Reply form */}
        <div className="flex flex-col gap-2 border-t border-gray-100 pt-3 dark:border-gray-700">
          <MDEditor
            className={editorClass}
            data-color-mode={isDarkMode ? 'dark' : 'light'}
            value={reply}
            onChange={(v) => setReply(v ?? '')}
            textareaProps={{
              ...pasteDropProps,
              disabled: isSending || isUploading,
              placeholder: t('istifta.replyPlaceholder'),
            }}
            commands={[
              commands.bold,
              commands.italic,
              commands.strikethrough,
              commands.divider,
              commands.link,
              commands.quote,
              commands.unorderedListCommand,
              commands.orderedListCommand,
              commands.divider,
              uploadImageCommand,
            ]}
            extraCommands={[]}
            preview={isPreviewMode ? 'preview' : 'edit'}
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              color="gray"
              onClick={() => setIsPreviewMode((p) => !p)}
            >
              {isPreviewMode
                ? t('technicalSupport.editMode')
                : t('technicalSupport.previewMode')}
            </Button>
            <Button
              size="sm"
              className="btn btn-light ms-auto"
              disabled={!reply.trim() || isSending || isUploading || !userInfo}
              onClick={handleSendReply}
              isProcessing={isSending}
            >
              {t('istifta.sendReply')}
            </Button>
          </div>
        </div>
      </section>

      {/* ── Ticket meta ── */}
      <Accordion className="border-primary-100">
        <Accordion.Panel>
          <Accordion.Title className="accordion">
            {t('istifta.ticketDetails')}
          </Accordion.Title>
          <Accordion.Content className="bg-transparent p-4 dark:bg-transparent">
            <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <MetaRow
                label={t('istifta.ticketNumber')}
                value={ticket.ticketNumber}
              />
              {ticket.userEmail && (
                <MetaRow
                  label={t('technicalSupport.userEmail')}
                  value={ticket.userEmail}
                />
              )}
              {ticket.mobileNumber && (
                <MetaRow
                  label={t('technicalSupport.mobileNumber')}
                  value={ticket.mobileNumber}
                />
              )}
              <MetaRow
                label={t('istifta.submittedAt')}
                value={new Date(ticket.createdAt).toLocaleString()}
              />
              <MetaRow
                label={t('istifta.status')}
                value={t(
                  'technicalSupport.statusList.' + ticket.status.toLowerCase()
                )}
              />
            </dl>
          </Accordion.Content>
        </Accordion.Panel>
      </Accordion>
    </div>
  );
};

export default IstiftaDetailsPage;

const MetaRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex flex-row items-center justify-between gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-900/40">
    <dt className="text-sm font-medium text-gray-600 dark:text-gray-400">
      {label}
    </dt>
    <dd className="text-sm font-semibold text-gray-900 dark:text-white">
      {value}
    </dd>
  </div>
);
